import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ImagePlus, Loader2, Medal, RotateCcw, Search, Trophy, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../../lib/api";

const PACKS = [
  { id: "participation", label: "Bronze", Icon: Medal },
  { id: "classique", label: "Argent", Icon: Trophy },
  { id: "prestige", label: "Or", Icon: Award },
];

const PHOTO_COUNT = { participation: 1, classique: 2, prestige: 4 };  // emplacements photo par carte

const SLOTS = [
  { key: "medal", label: "Médaille du forfait", Icon: Medal },
  { key: "trophy", label: "Trophée du forfait", Icon: Trophy },
  { key: "plaque", label: "Plaque du forfait", Icon: Award },
];

// Attrait des forfaits tournoi + CHOIX des produits de référence (médaille, trophée,
// plaque) qui fixent les prix des forfaits sur la page publique /forfaits.
export default function ForfaitStats() {
  const [data, setData] = useState(null);
  const [refs, setRefs] = useState(null);              // produits actuellement utilisés
  const [search, setSearch] = useState({});            // texte de recherche par emplacement
  const [results, setResults] = useState({});          // résultats par emplacement
  const [busy, setBusy] = useState("");
  const [prices, setPrices] = useState({});            // prix médaille/joueur par forfait (modifiables)
  const [mins, setMins] = useState({});                // minimum de joueurs par forfait (modifiables)

  const loadStats = () => api.get("/admin/forfaits/stats").then((r) => setData(r.data)).catch(() => setData({ par_forfait: {}, total: 0, sept_jours: 0 }));
  const loadRefs = () => api.get("/forfaits").then((r) => setRefs(r.data)).catch(() => setRefs({}));
  const loadPrices = () => api.get("/admin/settings").then((r) => {
    setPrices({
      participation: r.data.forfait_price_participation ?? "",
      classique: r.data.forfait_price_classique ?? "",
      prestige: r.data.forfait_price_prestige ?? "",
    });
    setMins({
      participation: r.data.forfait_min_participation ?? "",
      classique: r.data.forfait_min_classique ?? "",
      prestige: r.data.forfait_min_prestige ?? "",
    });
  }).catch(() => {});
  useEffect(() => { loadStats(); loadRefs(); loadPrices(); }, []);

  // Prix de la part médaille par joueur, modifiable par forfait (vide = prix officiel)
  const savePrice = async (pkg) => {
    setBusy(`price-${pkg}`);
    try {
      const raw = String(prices[pkg] ?? "").trim().replace(",", ".");
      const value = raw === "" ? null : parseFloat(raw);
      if (value !== null && (isNaN(value) || value < 0)) throw new Error("Prix invalide");
      await api.patch("/admin/settings", { [`forfait_price_${pkg}`]: value });
      toast.success(value === null
        ? "Prix officiel rétabli (Bronze 10,95 $ / Argent 14,95 $ / Or 19,95 $)"
        : `Prix du forfait mis à jour : ${value.toFixed(2)} $ par joueur — la page /forfaits suit immédiatement`);
      loadRefs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };

  // Minimum de joueurs (minimum de commande) par forfait — vide = minimum officiel
  const saveMin = async (pkg) => {
    setBusy(`min-${pkg}`);
    try {
      const raw = String(mins[pkg] ?? "").trim();
      const value = raw === "" ? null : parseInt(raw, 10);
      if (value !== null && (isNaN(value) || value < 1 || value > 500)) throw new Error("Minimum invalide (1 à 500)");
      await api.patch("/admin/settings", { [`forfait_min_${pkg}`]: value });
      toast.success(value === null
        ? "Minimum officiel rétabli (Bronze 12 / Argent 24 / Or 24 joueurs)"
        : `Minimum du forfait mis à jour : ${value} joueurs — la page /forfaits suit immédiatement`);
      loadRefs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };

  const searchProducts = async (slot) => {
    const q = (search[slot] || "").trim();
    if (q.length < 2) return;
    setBusy(`s-${slot}`);
    try {
      const { data: d } = await api.get("/admin/products", { params: { q, limit: 8 } });
      setResults((r) => ({ ...r, [slot]: d.products || d.items || [] }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const pick = async (slot, productId) => {
    setBusy(`p-${slot}`);
    try {
      await api.patch("/admin/settings", { [`forfait_${slot}_id`]: productId });
      toast.success(productId
        ? "Produit du forfait mis à jour — la page /forfaits suit immédiatement"
        : "Choix automatique rétabli (produit le moins cher de la catégorie)");
      setResults((r) => ({ ...r, [slot]: [] }));
      setSearch((s) => ({ ...s, [slot]: "" }));
      loadRefs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  // Photos des cartes : 1 (Bronze), 2 (Argent), 4 (Or) — choisies par l'admin
  const pickPhoto = async (pkg, i, productId) => {
    setBusy(`pp-${pkg}-${i}`);
    try {
      await api.patch("/admin/settings", { [`forfait_photo_${pkg}_${i}`]: productId });
      toast.success(productId
        ? "Photo de la carte mise à jour — visible sur la page /forfaits"
        : "Photo par défaut rétablie (médaille / trophée de référence)");
      setResults((r) => ({ ...r, [`photo-${pkg}-${i}`]: [] }));
      setSearch((s) => ({ ...s, [`photo-${pkg}-${i}`]: "" }));
      loadRefs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!data) return null;
  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-6 mb-8" data-testid="forfait-stats">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-[11px] uppercase tracking-widest text-gold">Soumissions de forfaits — attrait mesuré</p>
        <span className="text-xs text-ivory/55" data-testid="forfait-stats-total">
          <strong className="text-champagne">{data.total}</strong> au total
          · <strong className="text-emerald-400">{data.sept_jours}</strong> sur 7 jours
        </span>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {PACKS.map(({ id, label, Icon }) => {
          const s = data.par_forfait[id] || { count: 0, total_estime: 0, traitees: 0 };
          return (
            <div key={id} data-testid={`forfait-stat-${id}`}
              className="bg-ink border border-ivory/10 rounded-xl p-5 hover:border-gold/30 transition-colors">
              <div className="flex items-center gap-2.5 mb-2">
                <Icon className="w-5 h-5 text-gold" />
                <span className="font-semibold text-ivory/90">{label}</span>
                {s.count > 0 && s.traitees < s.count && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/30 rounded-full px-2 py-0.5">
                    {s.count - s.traitees} en attente
                  </span>
                )}
              </div>
              <p className="font-serif text-3xl text-champagne">{s.count} <span className="text-sm text-ivory/50">reçue{s.count > 1 ? "s" : ""}</span></p>
              <p className="text-xs text-ivory/55 mt-1 inline-flex items-center gap-1.5">
                <TrendingUp size={12} className="text-gold/70" /> {fmt(s.total_estime)} de valeur estimée
              </p>
            </div>
          );
        })}
      </div>

      {/* Choix des produits de référence des forfaits */}
      <div className="mt-6 border-t border-ivory/10 pt-5" data-testid="forfait-product-picks">
        <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Produits des forfaits</p>
        <p className="text-xs text-ivory/55 mb-4">
          Choisissez la médaille, le trophée et la plaque qui fixent les prix des forfaits sur la page publique
          (le prix affiché = prix du produit + sa gravure, rabais de quantité appliqués). Sans choix, le produit
          le moins cher de la catégorie est utilisé automatiquement.
        </p>
        <div className="space-y-4">
          {SLOTS.map(({ key, label, Icon }) => {
            const cur = refs?.[key];
            return (
              <div key={key} className="bg-ink border border-ivory/10 rounded-xl p-4" data-testid={`forfait-pick-${key}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <Icon className="w-4 h-4 text-gold shrink-0" />
                  <span className="text-sm font-semibold text-ivory/90">{label}</span>
                  <span className="text-xs text-ivory/60" data-testid={`forfait-pick-current-${key}`}>
                    {cur ? <>actuel : <strong className="text-champagne">{cur.name}</strong> — {fmt(cur.base_price)} + {fmt(cur.gravure)} gravure
                      {cur.catalogue_no ? <span className="text-ivory/40"> · cat. {cur.catalogue_no}</span> : null}</> : "…"}
                  </span>
                  <form className="ml-auto flex items-center gap-2"
                    onSubmit={(e) => { e.preventDefault(); searchProducts(key); }}>
                    <input
                      value={search[key] || ""}
                      onChange={(e) => setSearch((s) => ({ ...s, [key]: e.target.value }))}
                      placeholder="Changer : nom du produit…"
                      data-testid={`forfait-pick-search-${key}`}
                      className="bg-ink border border-ivory/15 rounded-full px-3.5 py-1.5 text-xs text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none w-52"
                    />
                    <button type="submit" disabled={busy === `s-${key}`} data-testid={`forfait-pick-search-btn-${key}`}
                      className="p-2 border border-ivory/20 rounded-full text-ivory/60 hover:text-gold hover:border-gold/50 transition-colors">
                      {busy === `s-${key}` ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                    </button>
                    <button type="button" onClick={() => pick(key, "")} disabled={busy === `p-${key}`}
                      data-testid={`forfait-pick-auto-${key}`} title="Revenir au choix automatique (moins cher de la catégorie)"
                      className="inline-flex items-center gap-1 text-[10px] text-ivory/50 hover:text-gold border border-ivory/15 rounded-full px-2.5 py-1.5 transition-colors">
                      <RotateCcw size={11} /> Auto
                    </button>
                  </form>
                </div>
                {(results[key] || []).length > 0 && (
                  <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid={`forfait-pick-results-${key}`}>
                    {results[key].map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => pick(key, p.id)} disabled={busy === `p-${key}`}
                          data-testid={`forfait-pick-choose-${key}-${p.id}`}
                          className="w-full flex items-center gap-3 text-left border border-ivory/10 rounded-lg px-3 py-2 hover:border-gold/50 transition-colors">
                          {p.image && <img src={p.image} alt="" className="w-9 h-9 object-contain rounded bg-ink shrink-0" />}
                          <span className="min-w-0">
                            <span className="block text-xs text-ivory/85 truncate">{p.name}</span>
                            <span className="block text-[10px] text-ivory/50">
                              {fmt(p.base_price)}{p.sku ? ` · ${p.sku}` : ""}{p.catalogue_no ? ` · cat. ${p.catalogue_no}` : ""}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Photos des cartes forfaits : 1 (Bronze), 2 (Argent), 4 (Or) */}
      <div className="mt-6 border-t border-ivory/10 pt-5" data-testid="forfait-photos-pick">
        <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Photos des cartes forfaits</p>
        <p className="text-xs text-ivory/55 mb-4">
          Bronze : 1 photo · Argent : 2 photos · Or : 4 photos. Choisissez un produit par emplacement ;
          sans choix, la médaille puis le trophée de référence sont utilisés.
        </p>
        <div className="space-y-4">
          {PACKS.map(({ id, label }) => (
            <div key={id} className="bg-ink border border-ivory/10 rounded-xl p-4" data-testid={`forfait-photos-${id}`}>
              <p className="text-sm font-semibold text-ivory/90 mb-3">
                {label} <span className="text-ivory/40 font-normal">— {PHOTO_COUNT[id]} emplacement{PHOTO_COUNT[id] > 1 ? "s" : ""}</span>
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Array.from({ length: PHOTO_COUNT[id] }, (_, i) => {
                  const slotKey = `photo-${id}-${i}`;
                  const cur = refs?.photos?.[id]?.[i] || "";
                  return (
                    <div key={i} className="min-w-0" data-testid={`forfait-photo-slot-${id}-${i}`}>
                      <div className="flex items-center gap-2.5">
                        <span className="w-11 h-11 rounded-lg border border-gold/25 bg-surface flex items-center justify-center overflow-hidden shrink-0">
                          {cur
                            ? <img src={cur} alt="" className="w-full h-full object-cover" />
                            : <ImagePlus size={15} className="text-ivory/30" />}
                        </span>
                        <form className="flex items-center gap-1.5 min-w-0"
                          onSubmit={(e) => { e.preventDefault(); searchProducts(slotKey); }}>
                          <input value={search[slotKey] || ""}
                            onChange={(e) => setSearch((s) => ({ ...s, [slotKey]: e.target.value }))}
                            placeholder={`Photo ${i + 1} : nom du produit…`}
                            data-testid={`forfait-photo-search-${id}-${i}`}
                            className="bg-surface border border-ivory/15 rounded-full px-3 py-1.5 text-xs text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none w-40" />
                          <button type="submit" disabled={busy === `s-${slotKey}`} data-testid={`forfait-photo-search-btn-${id}-${i}`}
                            className="p-2 border border-ivory/20 rounded-full text-ivory/60 hover:text-gold hover:border-gold/50 transition-colors shrink-0">
                            {busy === `s-${slotKey}` ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                          </button>
                          <button type="button" onClick={() => pickPhoto(id, i, "")} disabled={busy === `pp-${id}-${i}`}
                            data-testid={`forfait-photo-reset-${id}-${i}`} title="Revenir à l'image par défaut"
                            className="inline-flex items-center text-[10px] text-ivory/50 hover:text-gold border border-ivory/15 rounded-full px-2 py-1.5 transition-colors shrink-0">
                            <RotateCcw size={11} />
                          </button>
                        </form>
                      </div>
                      {(results[slotKey] || []).length > 0 && (
                        <ul className="mt-2 space-y-1.5" data-testid={`forfait-photo-results-${id}-${i}`}>
                          {results[slotKey].map((p) => (
                            <li key={p.id}>
                              <button type="button" onClick={() => pickPhoto(id, i, p.id)} disabled={busy === `pp-${id}-${i}`}
                                data-testid={`forfait-photo-choose-${id}-${i}-${p.id}`}
                                className="w-full flex items-center gap-3 text-left border border-ivory/10 rounded-lg px-3 py-2 hover:border-gold/50 transition-colors">
                                {p.image && <img src={p.image} alt="" className="w-9 h-9 object-contain rounded bg-surface shrink-0" />}
                                <span className="min-w-0">
                                  <span className="block text-xs text-ivory/85 truncate">{p.name}</span>
                                  <span className="block text-[10px] text-ivory/50">{fmt(p.base_price)}{p.sku ? ` · ${p.sku}` : ""}</span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prix par joueur + minimum de joueurs par forfait (modifiables) */}
      <div className="mt-6 border-t border-ivory/10 pt-5" data-testid="forfait-prices">
        <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Prix par joueur et minimum de commande</p>
        <p className="text-xs text-ivory/55 mb-4">
          Officiels : 10,95 $ (Bronze, min. 12 joueurs), 14,95 $ (Argent, min. 24 — médailles + trophée
          d'équipe au choix), 19,95 $ (Or, min. 24 — trophée par joueur, sans médaille) — ruban 7/8 po,
          insertion avant avec logo et inscription arrière personnalisée incluses pour les médailles.
          Aucun rabais de quantité automatique ; la mention « Les prix peuvent varier selon la quantité »
          est affichée aux clients. Modifiez puis OK ; videz le champ pour revenir à l'officiel.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {PACKS.map(({ id, label }) => (
            <div key={id} className="bg-ink border border-ivory/10 rounded-lg p-3 space-y-2.5">
              <p className="text-xs text-ivory/65 font-semibold">{label}</p>
              <div className="flex items-center gap-2">
                <label htmlFor={`forfait-price-${id}`} className="text-[10px] text-ivory/50 flex-1">Prix / joueur $</label>
                <input id={`forfait-price-${id}`} data-testid={`forfait-price-${id}`} type="text" inputMode="decimal"
                  value={prices[id] ?? ""} placeholder={id === "participation" ? "10,95" : id === "classique" ? "14,95" : "19,95"}
                  onChange={(e) => setPrices((p) => ({ ...p, [id]: e.target.value }))}
                  className="w-20 bg-surface border border-ivory/15 rounded px-2 py-1.5 text-xs text-right focus:border-gold focus:outline-none" />
                <button type="button" data-testid={`forfait-price-save-${id}`} onClick={() => savePrice(id)}
                  disabled={busy === `price-${id}`}
                  className="text-xs border border-gold/40 text-gold px-2.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">
                  {busy === `price-${id}` ? <Loader2 size={11} className="animate-spin" /> : "OK"}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor={`forfait-min-${id}`} className="text-[10px] text-ivory/50 flex-1">Minimum de joueurs</label>
                <input id={`forfait-min-${id}`} data-testid={`forfait-min-${id}`} type="text" inputMode="numeric"
                  value={mins[id] ?? ""} placeholder={id === "participation" ? "12" : "24"}
                  onChange={(e) => setMins((m) => ({ ...m, [id]: e.target.value }))}
                  className="w-20 bg-surface border border-ivory/15 rounded px-2 py-1.5 text-xs text-right focus:border-gold focus:outline-none" />
                <button type="button" data-testid={`forfait-min-save-${id}`} onClick={() => saveMin(id)}
                  disabled={busy === `min-${id}`}
                  className="text-xs border border-gold/40 text-gold px-2.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">
                  {busy === `min-${id}` ? <Loader2 size={11} className="animate-spin" /> : "OK"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-ivory/45 mt-4">
        Les demandes d'appel du site s'ouvrent dans « Ventes &amp; Soumissions » (bouton « Demandes d'appel ») — la page publique : <Link to="/forfaits" className="text-gold hover:underline">/forfaits</Link>
      </p>
    </div>
  );
}
